/**
 * The FinOps tab's and the Cost gadget's formatting only. Every figure is computed by vfs-service's FinOps model
 * (src/finops, `GET /api/v1/projects/:id/finops`: services/FinOpsService.js), the same the vfs_finops AI tool answers,
 * with the prices and assumptions the Flow admin sets (Settings › FinOps). Nothing here computes a cost.
 */

const round2 = (n) => Math.round(n * 100) / 100;

/** "€146", "€0.49", "$4,900" (the currency's symbol, € by default); "—" when unknown. Cents under 10, or as asked. */
export function money(value, { cents = null, symbol = '€' } = {}) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) { return '—'; }
  const v = Number(value);
  const withCents = cents ?? Math.abs(v) < 10;
  return `${symbol}${v.toLocaleString('en-US', { minimumFractionDigits: withCents ? 2 : 0, maximumFractionDigits: withCents ? 2 : 0 })}`;
}

/** "1k", "100k", "1M". */
export function shortCount(n) {
  const v = Number.isFinite(Number(n)) ? Number(n) : 0;
  if (v >= 1e6) { return `${round2(v / 1e6)}M`; }
  if (v >= 1e3) { return `${round2(v / 1e3)}k`; }
  return `${v}`;
}

/** "1.2", "0.35", "12" credits; "—" when unknown. */
export function credits(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) { return '—'; }
  const v = Number(value);
  return v >= 10 ? `${Math.round(v)}` : v >= 1 ? v.toFixed(1) : v.toFixed(2);
}

/** "512 MB", "1.5 GB"; "—" when unknown. */
export const gb = (v) => (v === null || v === undefined ? '—' : v < 1 ? `${Math.round(v * 1024)} MB` : `${v.toFixed(v < 10 ? 1 : 0)} GB`);
/** "0.30 vCPU", "2.0 vCPU"; "—" when unknown. */
export const cores = (v) => (v === null || v === undefined ? '—' : `${v < 1 ? v.toFixed(2) : v.toFixed(1)} vCPU`);

/** The requests-a-day slider's stops (a range input over their index). */
export const REQUEST_STEPS = [100, 300, 1_000, 3_000, 10_000, 30_000, 100_000, 300_000, 1_000_000, 3_000_000];

/** The slider stop nearest to `requestsPerDay` (on a log scale). */
export function requestStepIndex(requestsPerDay) {
  const r = Math.max(Number(requestsPerDay) || 0, 1);
  let best = 0;
  REQUEST_STEPS.forEach((step, i) => { if (Math.abs(Math.log10(step) - Math.log10(r)) < Math.abs(Math.log10(REQUEST_STEPS[best]) - Math.log10(r))) { best = i; } });
  return best;
}
